import React, { ComponentProps, useState, useEffect, useContext } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
// Components
import { Form } from 'react-bootstrap'
import { Col } from 'react-bootstrap'
import FormItem from 'components/utils/FormItem'
import FormDropdown from 'components/utils/FormDropdown'
import LinkToButton from 'components/utils/Button/LinkToButton'
import Header from 'components/utils/Form/Header'
import Footer from 'components/utils/Form/Footer'
// Styles
import { ItemWrapper, OffsetedContainer } from 'styles/common'
// Additional
import { PlayerRecord } from 'data/PlayerRecord'
import { AppContext } from 'App'
import { PlayerContext } from 'components/Player/__Players.context'
import { MatchContext } from 'components/Matches/__Matches.context'
import { FormContext } from 'components/utils/Form.context'
import { Item } from 'components/utils/Dropdown'
import { Form as FormHelper } from 'helpers/form'
import { Data as DataHelper } from 'helpers/data'
import { Routing as RoutingHelper } from 'helpers/routing'

const SubmitKeys = [
  'match_id',
  'team_id',
  'sportsman_id',
  'sportsman_role_id',
] as Array<keyof PlayerRecord>

type ValidationKeys = Record<
  keyof Pick<PlayerRecord, 'team_id' | 'sportsman_id'>,
  any
>

const PlayerForm: React.FC<FormHelper.Props<PlayerRecord>> = (p) => {
  const { db, alert, history } = useContext(AppContext)
  const { params: p_params } = useContext(PlayerContext)
  const { params: m_params } = useContext(MatchContext)

  const [form, setForm] = useState<{
    data?: PlayerRecord
    state: FormHelper.FormState
  }>({ data: p.data, state: p.state ?? 'new' })

  const opt = {
    alert: {
      manager: alert,
      texts: {
        form: 'Player',
      },
    },
    history: history,
    lock: {
      callback: p.locked?.callback,
    },
  }

  const formik = useFormik({
    initialValues: {
      ...form.data,

      match_id: form.data?.match?.id ?? p_params?.match_id,

      team_id: form.data?.team?.id,
      team_text: form.data?.team?.full_name,

      sportsman_id: form.data?.sportsman?.id,
      sportsman_text: form.data?.sportsman_nickname,

      sportsman_role_id: form.data?.sportsman_role?.id,
      sportsman_role_text: form.data?.sportsman_role?.ident,
    },

    onSubmit: async (values) => {
      if (!db?.players) return

      try {
        const data = await FormHelper.submitChanges<PlayerRecord>({
          action: form.state === 'new' ? 'create' : 'update',
          keys: SubmitKeys,
          data: Object.assign(new PlayerRecord(), values ?? {}),
          repository: db.players,
          optional: opt,
        })

        if (data?.id) {
          setForm({ data: data, state: 'existing' })

          history?.replace(
            RoutingHelper.getPlayerDetailUrl({
              tournament:
                m_params?.tournament_id ?? RoutingHelper.NewRecordSymbol,
              match: m_params?.tournament_id ?? RoutingHelper.NewRecordSymbol,
              player: data.id,
            })
          )
        }

        data && p.submitCallback?.(data)
      } catch (err) {
        console.error(err)
      }
    },

    validationSchema: Yup.object<ValidationKeys>({
      team_id: Yup.string().required('Team is a required field'),
      sportsman_id: Yup.string().required('Sportsman is a required field'),
    }),
  })

  const approve = async () => {
    if (!db?.players) return

    await FormHelper.approve({
      data: Object.assign(new PlayerRecord(), formik.values ?? {}),
      repository: db.players,
      optional: opt,
    })
  }

  const unapprove = async () => {
    if (!db?.players) return

    await FormHelper.unapprove({
      data: Object.assign(new PlayerRecord(), formik.values ?? {}),
      repository: db.players,
      optional: opt,
    })
  }

  const delete_ = async () => {
    if (!db?.players) return

    await FormHelper.delete_({
      data: Object.assign(new PlayerRecord(), formik.values ?? {}),
      repository: db.players,
      optional: opt,
    })
  }

  const touch = async () => {
    if (!db?.players) return

    await FormHelper.touch({
      data: Object.assign(new PlayerRecord(), formik.values ?? {}),
      repository: db.players,
      optional: opt,
    })
  }

  const [team, setTeam] = useState<{
    value?: Item
    data?: Array<Item>
    loaded: boolean
  }>({
    value: {
      id: formik.values.team_id ?? '',
      text: formik.values.team_text,
    },
    loaded: false,
  })

  const [sportsman, setSportsman] = useState<{
    value?: Item
    data?: Array<Item>
    loaded: boolean
  }>({
    value: {
      id: formik.values.sportsman_id ?? '',
      text: formik.values.sportsman_text,
    },
    loaded: false,
  })

  const [sportsmanRole, setSportmanRole] = useState<{
    value?: Item
    data?: Array<Item>
    loaded: boolean
  }>({
    value: {
      id: formik.values.sportsman_role_id ?? '',
      text: formik.values.sportsman_role_text,
    },
    loaded: false,
  })

  const _setTeams = async () => {
    const _data = await DataHelper.getAllTeams(db!)

    setTeam((prev) => ({
      loaded: true,
      value: prev?.value,
      data: _data?.map((s) => {
        return { id: s.id ?? '', text: s.full_name ?? '' } as Item
      }),
    }))
  }

  const _setSportmen = async () => {
    const _data = await DataHelper.getAllSportsmen(db!)

    setSportsman((prev) => ({
      loaded: true,
      value: prev?.value,
      data: _data?.map((s) => {
        return {
          id: s.id ?? '',
          text: s.nickname ?? '',
        } as Item
      }),
    }))
  }

  const _setSportmanRole = async () => {
    const _data = await DataHelper.getAllSportsmanRoles(db!)

    setSportmanRole((prev) => ({
      loaded: true,
      value: prev?.value,
      data: _data?.map((s) => {
        return { id: s.id ?? '', text: s.ident ?? '' } as Item
      }),
    }))
  }

  const handleTeamSelect = (item: Item) => {
    formik.setFieldValue('team_id' as keyof PlayerRecord, item.id)

    setTeam((prev) => ({ ...prev, value: item, data: prev.data ?? [] }))
  }

  const handleSportsmanSelect = (item: Item) => {
    formik.setFieldValue('sportsman_id' as keyof PlayerRecord, item.id)

    setSportsman((prev) => ({ ...prev, value: item, data: prev.data ?? [] }))
  }

  const handleSportsmanRoleSelect = (item: Item) => {
    formik.setFieldValue('sportsman_role_id' as keyof PlayerRecord, item.id)

    setSportmanRole((prev) => ({ ...prev, value: item, data: prev.data ?? [] }))
  }

  useEffect(() => {
    _setTeams()
    _setSportmen()
    _setSportmanRole()
  }, [])

  //#region Form Components
  const _Match = (
    <Form.Control
      name={'match_id' as keyof PlayerRecord}
      value={formik.values.match_id}
      hidden
    />
  )

  const _SportsmanRole = (
    <FormItem
      label={<Form.Label>Sportsman Role</Form.Label>}
      input={
        <FormDropdown
          name={'sportsman_role_id' as keyof PlayerRecord}
          items={sportsmanRole.data ?? []}
          onSelect={handleSportsmanRoleSelect}
          value={sportsmanRole.value}
          loading={!sportsmanRole.loaded}
        />
      }
    />
  )

  const _Team = (
    <FormItem
      label={<Form.Label>Team</Form.Label>}
      input={
        <>
          <FormDropdown
            name={'team_id' as keyof PlayerRecord}
            items={team.data ?? []}
            onSelect={handleTeamSelect}
            value={team.value}
            required
            isInvalid={formik.submitCount > 0 && !!formik.errors.team_id}
            loading={!team.loaded}
            append={
              <LinkToButton
                path={
                  formik.values.team_id &&
                  RoutingHelper.getTeamDetailUrl({
                    sport: RoutingHelper.NewRecordSymbol,
                    team: formik.values.team_id,
                  })
                }
              />
            }
          />
          <Form.Control.Feedback type={'invalid'}>
            {formik.errors.team_id}
          </Form.Control.Feedback>
        </>
      }
    />
  )

  const _Sportsman = (
    <FormItem
      label={<Form.Label>Sportsman</Form.Label>}
      input={
        <>
          <FormDropdown
            name={'sportsman_id' as keyof PlayerRecord}
            items={sportsman.data ?? []}
            onSelect={handleSportsmanSelect}
            value={sportsman.value}
            required
            isInvalid={formik.submitCount > 0 && !!formik.errors.sportsman_id}
            loading={!sportsman.loaded}
            append={
              <LinkToButton
                path={
                  formik.values.sportsman_id &&
                  RoutingHelper.getSportsmanDetailUrl({
                    sport: RoutingHelper.NewRecordSymbol,
                    sportsman: formik.values.sportsman_id,
                  })
                }
              />
            }
          />
          <Form.Control.Feedback type={'invalid'}>
            {formik.errors.sportsman_id}
          </Form.Control.Feedback>
        </>
      }
    />
  )
  //#endregion

  const _ComputeHeaderProps = (): ComponentProps<typeof Header> => {
    if (form.state === 'new') return {}

    return {
      useApprove: {
        defaultValue: !!p.data?.data_approved_at,
      },
      useTouch: false,
      locked: p.locked?.value,
    }
  }

  const _ComputeFooterProps = (): ComponentProps<typeof Footer> => {
    if (form.state === 'new')
      return {
        useSave: {},
      }

    return { useSave: {}, useDelete: {}, locked: p.locked?.value }
  }

  return (
    <OffsetedContainer>
      <FormContext.Provider
        value={{
          submit: formik.submitForm,
          delete: delete_,
          approve: approve,
          unapprove: unapprove,
          touch: touch,
        }}
      >
        <Form noValidate onSubmit={formik.handleSubmit}>
          {/* ----------- Hidden Inputs Section ----------- */}
          {_Match}
          {/* --------------------------------------------- */}

          <Header {..._ComputeHeaderProps()} />

          <Form.Row>
            <Col as={ItemWrapper}>{_Team}</Col>
          </Form.Row>

          <ItemWrapper />

          <Form.Row>
            <Col as={ItemWrapper}>{_Sportsman}</Col>
          </Form.Row>

          <ItemWrapper />

          <Form.Row>
            <Col as={ItemWrapper}>{_SportsmanRole}</Col>
          </Form.Row>

          <Footer {..._ComputeFooterProps()} />
        </Form>
      </FormContext.Provider>
    </OffsetedContainer>
  )
}

export default PlayerForm
